import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Globe, 
  DollarSign, 
  CreditCard, 
  Zap,
  Bell,
  Save,
  Clock,
  Loader2,
  Mail,
  Wallet,
  Send,
  Lock,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  RotateCcw,
  Download,
  Upload,
  Trash2,
  Unlock,
  MessageSquare,
  FileText,
  Info,
  HelpCircle,
  Database,
  Shield,
  Server,
  Smartphone,
  ChevronDown,
  Settings2,
  Plus,
  Pencil
} from 'lucide-react';
import { MapPin, Plug, Receipt } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tab titles
const TAB_TITLES = {
  system: { title: 'Sistemi', description: 'Konfigurime të përgjithshme të sistemit', icon: Globe },
  pricing: { title: 'Çmimet', description: 'Tarifat e karikimit dhe pagesave', icon: DollarSign },
  payments: { title: 'Pagesat', description: 'Procesorët e pagesave', icon: CreditCard },
  ocpp: { title: 'OCPP', description: 'Protokolli Open Charge Point', icon: Zap },
  notifications: { title: 'Njoftimet', description: 'Konfigurime të njoftimeve push dhe email', icon: Bell },
  topup: { title: 'Top-up', description: 'Paketat e mbushjes cash', icon: Wallet },
  email: { title: 'Email', description: 'Konfigurime SMTP për dërgim email-esh', icon: Mail },
  connectors: { title: 'Konektorë', description: 'Llojet e portave të karikimit', icon: Plug },
  integrations: { title: 'Integrime', description: 'Google Maps dhe integrime të tjera', icon: MapPin },
  appversion: { title: 'App Version', description: 'Kontrolli i versionit të aplikacionit mobil', icon: Smartphone },
};

// OCPP Commands
const OCPP_COMMANDS = [
  { id: 'reset', name: 'Reset', icon: RotateCcw, description: 'Rinis karikuesin', details: 'Dërgon Reset.req - Soft rinis softuerin, Hard bën ristartim të plotë.', type: 'danger', params: ['Soft', 'Hard'] },
  { id: 'unlock', name: 'Unlock Connector', icon: Unlock, description: 'Zhbllokon kabllon', details: 'UnlockConnector.req - Liron fizikisht kabllon e karikimit.', type: 'warning', params: ['Connector ID'] },
  { id: 'clearCache', name: 'Clear Cache', icon: Trash2, description: 'Pastron cache-in', details: 'ClearCache.req - Pastron listën lokale të autorizimeve.', type: 'secondary' },
  { id: 'getDiagnostics', name: 'Get Diagnostics', icon: Download, description: 'Shkarkon log-et', details: 'GetDiagnostics.req - Merr log-et e karikuesit në FTP.', type: 'info', params: ['FTP URL'] },
  { id: 'updateFirmware', name: 'Update Firmware', icon: Upload, description: 'Përditëson firmware', details: 'UpdateFirmware.req - Instalon version të ri të softuerit.', type: 'info', params: ['URL'] },
  { id: 'triggerMessage', name: 'Trigger Message', icon: MessageSquare, description: 'Kërkon mesazh', details: 'TriggerMessage.req - Kërkon BootNotification, StatusNotification, etj.', type: 'secondary', params: ['Type'] },
  { id: 'getConfiguration', name: 'Get Configuration', icon: FileText, description: 'Lexon konfig', details: 'GetConfiguration.req - Merr parametrat e karikuesit.', type: 'primary', params: ['Key'] },
  { id: 'changeConfiguration', name: 'Change Configuration', icon: Settings2, description: 'Ndryshon parametër', details: 'ChangeConfiguration.req - Ndryshon një parametër specifik.', type: 'primary', params: ['Key', 'Value'] }
];

// OCPP Config Keys
const OCPP_CONFIG_KEYS = [
  { key: 'HeartbeatInterval', description: 'Intervali heartbeat', unit: 's', rw: 'RW' },
  { key: 'ConnectionTimeOut', description: 'Timeout lidhje kablli', unit: 's', rw: 'RW' },
  { key: 'MeterValueSampleInterval', description: 'Intervali leximit energji', unit: 's', rw: 'RW' },
  { key: 'LocalAuthListEnabled', description: 'Lista lokale autorizimi', unit: 'bool', rw: 'RW' },
  { key: 'AuthorizeRemoteTxRequests', description: 'Autorizim remote', unit: 'bool', rw: 'RW' },
];

const Settings = () => {
  const { user, token } = useAuth();
  const { tab } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState([]);
  const [timezones, setTimezones] = useState([]);
  const [modified, setModified] = useState({});
  
  const activeTab = tab || 'system';
  const currentTabInfo = TAB_TITLES[activeTab] || TAB_TITLES.system;
  
  // Email config state
  const [emailConfig, setEmailConfig] = useState({
    smtp_host: '', smtp_port: 465, smtp_user: '', smtp_password: '',
    smtp_use_ssl: true, from_email: '', from_name: 'AlbMove', admin_email: ''
  });
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailTesting, setEmailTesting] = useState(false);
  const [emailTestResult, setEmailTestResult] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [emailModified, setEmailModified] = useState(false);
  
  // POK config state
  const [pokConfig, setPokConfig] = useState({
    key_id: '', key_secret: '', merchant_id: '', is_sandbox: true,
    webhook_url: '', redirect_url: '', fail_redirect_url: '', auto_capture: true
  });
  const [pokLoading, setPokLoading] = useState(false);
  const [pokTesting, setPokTesting] = useState(false);
  const [pokTestResult, setPokTestResult] = useState(null);
  const [showPokSecret, setShowPokSecret] = useState(false);
  const [pokModified, setPokModified] = useState(false);
  
  // Stripe config state
  const [stripeConfig, setStripeConfig] = useState({
    secret_key: '', publishable_key: '', webhook_secret: '', is_sandbox: true
  });
  const [stripeLoading, setStripeLoading] = useState(false);
  const [showStripeSecret, setShowStripeSecret] = useState(false);
  const [stripeModified, setStripeModified] = useState(false);
  
  // PayPal config state
  const [paypalConfig, setPaypalConfig] = useState({
    client_id: '', client_secret: '', is_sandbox: true
  });
  const [paypalLoading, setPaypalLoading] = useState(false);
  const [showPaypalSecret, setShowPaypalSecret] = useState(false);
  const [paypalModified, setPaypalModified] = useState(false);
  
  // Google Maps API Key state
  const [googleMapsApiKey, setGoogleMapsApiKey] = useState('');
  const [googleMapsLoading, setGoogleMapsLoading] = useState(false);
  const [googleMapsModified, setGoogleMapsModified] = useState(false);
  const [showGoogleMapsKey, setShowGoogleMapsKey] = useState(false);
  const [googleMapsTesting, setGoogleMapsTesting] = useState(false);
  const [googleMapsTestResult, setGoogleMapsTestResult] = useState(null);
  
  // DevPOS Fiscalization state
  const [devposConfig, setDevposConfig] = useState({
    api_url: 'https://demo.devpos.al',
    tenant: '',
    username: '',
    password: '',
    tcr_code: '',
    business_unit_code: '',
    operator_code: '',
    seller_name: 'C.S.M. shpk',
    seller_nuis: 'M21612019M',
    seller_address: 'Rruga Mucaj, Vore, 1032',
    seller_town: 'Tiranë',
    seller_country: 'ALB',
    is_enabled: false
  });
  const [devposLoading, setDevposLoading] = useState(false);
  const [devposTesting, setDevposTesting] = useState(false);
  const [devposTestResult, setDevposTestResult] = useState(null);
  const [showDevposPassword, setShowDevposPassword] = useState(false);
  const [devposModified, setDevposModified] = useState(false);
  
  // Connector types state
  const [connectorTypes, setConnectorTypes] = useState([]);
  const [connectorTypesLoading, setConnectorTypesLoading] = useState(false);
  const [editingConnectorType, setEditingConnectorType] = useState(null);
  const [newConnectorType, setNewConnectorType] = useState({ name: '', code: '', description: '', max_power_kw: 22, current_type: 'AC', is_active: true });
  const [showAddConnectorType, setShowAddConnectorType] = useState(false);
  const [deleteConnectorType, setDeleteConnectorType] = useState(null);
  const [deletingConnector, setDeletingConnector] = useState(false);
  
  // Expanded payment processor
  const [expandedProcessor, setExpandedProcessor] = useState(null);
  
  // App Version Config state
  const [appVersionConfig, setAppVersionConfig] = useState({
    min_version: '1.0.0',
    latest_version: '1.0.0',
    update_url_android: 'https://play.google.com/store/apps/details?id=al.albmove.app',
    update_url_ios: 'https://apps.apple.com/app/albmove/id123456789',
    force_update: true,
    update_message: 'Keni një version të vjetër të aplikacionit. Ju lutem përditësoni për të vazhduar.'
  });
  const [appVersionLoading, setAppVersionLoading] = useState(false);
  const [appVersionModified, setAppVersionModified] = useState(false);
  
  const canEdit = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';

  useEffect(() => {
    fetchSettings();
    fetchEmailConfig();
    fetchPokConfig();
    fetchGoogleMapsApiKey();
    fetchDevposConfig();
    fetchConnectorTypes();
    fetchAppVersionConfig();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/admin/settings`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error('Failed to fetch settings');
      const data = await response.json();
      setSettings(data.settings || []);
      setTimezones(data.available_timezones || []);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Gabim gjatë ngarkimit');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/email-config`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) setEmailConfig(await response.json());
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchGoogleMapsApiKey = async () => {
    try {
      // Get from settings
      const response = await fetch(`${API_URL}/api/admin/settings`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        const mapsSetting = (data.settings || []).find(s => s.key === 'integrations.google_maps_api_key');
        if (mapsSetting) {
          setGoogleMapsApiKey(mapsSetting.value || '');
        }
      }
    } catch (error) {
      console.error('Error fetching Google Maps API Key:', error);
    }
  };

  const saveGoogleMapsApiKey = async () => {
    try {
      setGoogleMapsLoading(true);
      const response = await fetch(`${API_URL}/api/admin/settings`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ settings: { 'integrations.google_maps_api_key': googleMapsApiKey } })
      });
      if (!response.ok) throw new Error('Failed');
      toast.success('Google Maps API Key u ruajt me sukses');
      setGoogleMapsModified(false);
      setGoogleMapsTestResult(null); // Reset test result after save
    } catch (error) {
      toast.error('Gabim gjatë ruajtjes');
    } finally {
      setGoogleMapsLoading(false);
    }
  };

  const testGoogleMapsApiKey = async () => {
    if (!googleMapsApiKey) {
      toast.error('Vendos API Key përpara testimit');
      return;
    }
    
    try {
      setGoogleMapsTesting(true);
      setGoogleMapsTestResult(null);
      const response = await fetch(`${API_URL}/api/v1/client/config/maps-api-key/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ api_key: googleMapsApiKey })
      });
      const result = await response.json();
      setGoogleMapsTestResult(result);
      
      if (result.success) {
        toast.success(result.message || 'API Key funksionon!');
      } else {
        toast.error(result.error || 'API Key nuk funksionon');
      }
    } catch (error) {
      setGoogleMapsTestResult({ success: false, error: 'Gabim gjatë testimit' });
      toast.error('Gabim gjatë testimit');
    } finally {
      setGoogleMapsTesting(false);
    }
  };

  // DevPOS Fiscalization functions
  const fetchDevposConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/devpos-config`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setDevposConfig(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Error fetching DevPOS config:', error);
    }
  };

  const saveDevposConfig = async () => {
    try {
      setDevposLoading(true);
      const response = await fetch(`${API_URL}/api/admin/devpos-config`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(devposConfig)
      });
      if (!response.ok) throw new Error('Failed');
      toast.success('Konfigurimi i DevPOS u ruajt me sukses');
      setDevposModified(false);
      setDevposTestResult(null);
    } catch (error) {
      toast.error('Gabim gjatë ruajtjes');
    } finally {
      setDevposLoading(false);
    }
  };

  const testDevposConnection = async () => {
    if (!devposConfig.tenant || !devposConfig.username || !devposConfig.password) {
      toast.error('Plotëso tenant, username dhe password');
      return;
    }
    
    try {
      setDevposTesting(true);
      setDevposTestResult(null);
      const response = await fetch(`${API_URL}/api/admin/devpos-config/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(devposConfig)
      });
      const result = await response.json();
      setDevposTestResult(result);
      
      if (result.success) {
        toast.success(result.message || 'Lidhja me DevPOS funksionon!');
      } else {
        toast.error(result.error || 'Lidhja me DevPOS dështoi');
      }
    } catch (error) {
      setDevposTestResult({ success: false, error: 'Gabim gjatë testimit' });
      toast.error('Gabim gjatë testimit');
    } finally {
      setDevposTesting(false);
    }
  };

  // Connector Types functions
  const fetchConnectorTypes = async () => {
    try {
      setConnectorTypesLoading(true);
      const response = await fetch(`${API_URL}/api/connector-types?include_inactive=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConnectorTypes(data.connector_types || []);
      }
    } catch (error) {
      console.error('Error fetching connector types:', error);
    } finally {
      setConnectorTypesLoading(false);
    }
  };

  const saveConnectorType = async (typeData, isNew = false) => {
    try {
      const url = isNew 
        ? `${API_URL}/api/connector-types`
        : `${API_URL}/api/connector-types/${typeData.id}`;
      
      const response = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(typeData)
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to save');
      }
      
      toast.success(isNew ? 'Lloji u krijua me sukses' : 'Lloji u përditësua');
      setEditingConnectorType(null);
      setShowAddConnectorType(false);
      setNewConnectorType({ name: '', code: '', description: '', max_power_kw: 22, current_type: 'AC', is_active: true });
      fetchConnectorTypes();
    } catch (error) {
      toast.error(error.message || 'Gabim gjatë ruajtjes');
    }
  };

  const toggleConnectorTypeActive = async (typeId, isActive) => {
    try {
      const response = await fetch(`${API_URL}/api/connector-types/${typeId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !isActive })
      });
      if (!response.ok) throw new Error('Failed');
      toast.success(isActive ? 'Lloji u çaktivizua' : 'Lloji u aktivizua');
      fetchConnectorTypes();
    } catch (error) {
      toast.error('Gabim gjatë ndryshimit');
    }
  };

  const uploadConnectorTypeImage = async (typeId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_URL}/api/connector-types/${typeId}/image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Upload failed');
      }
      
      toast.success('Imazhi u ngarkua me sukses');
      fetchConnectorTypes();
    } catch (error) {
      toast.error(error.message || 'Gabim gjatë ngarkimit');
    }
  };

  const handleDeleteConnectorType = async () => {
    if (!deleteConnectorType) return;
    setDeletingConnector(true);
    try {
      const r = await fetch(`${API_URL}/api/connector-types/${deleteConnectorType.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (r.ok) {
        toast.success(`"${deleteConnectorType.name}" u fshi me sukses`);
        setDeleteConnectorType(null);
        fetchConnectorTypes();
      } else {
        const err = await r.json();
        toast.error(err.detail || 'Gabim gjatë fshirjes');
      }
    } catch (e) {
      toast.error('Gabim: ' + e.message);
    } finally {
      setDeletingConnector(false);
    }
  };

  const getSettingValue = (key) => modified[key] !== undefined ? modified[key] : settings.find(s => s.key === key)?.value;
  const updateLocalSetting = (key, value) => setModified(prev => ({ ...prev, [key]: value }));
  const hasModifications = Object.keys(modified).length > 0;

  const saveSettings = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/api/admin/settings`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ settings: modified })
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false) {
        const firstError = Array.isArray(result.results)
          ? result.results.find((r) => !r.success)?.error
          : null;
        throw new Error(firstError || result.detail || 'Failed');
      }
      toast.success('U ruajt me sukses');
      setModified({});
      fetchSettings();
    } catch (error) {
      toast.error(error.message || 'Gabim gjatë ruajtjes');
    } finally {
      setSaving(false);
    }
  };

  const updateEmailConfig = (field, value) => { setEmailConfig(prev => ({ ...prev, [field]: value })); setEmailModified(true); };
  
  const saveEmailConfig = async () => {
    try {
      setEmailLoading(true);
      const response = await fetch(`${API_URL}/api/admin/email-config`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(emailConfig)
      });
      if (!response.ok) throw new Error('Failed');
      toast.success('Konfigurimi u ruajt');
      setEmailModified(false);
    } catch (error) {
      toast.error('Gabim gjatë ruajtjes');
    } finally {
      setEmailLoading(false);
    }
  };

  const testEmailConfig = async () => {
    try {
      setEmailTesting(true);
      setEmailTestResult(null);
      const response = await fetch(`${API_URL}/api/admin/email-config/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(emailConfig)
      });
      const result = await response.json();
      setEmailTestResult(result);
      toast[result.success ? 'success' : 'error'](result.success ? 'Email u dërgua!' : result.error);
    } catch (error) {
      setEmailTestResult({ success: false, error: error.message });
      toast.error('Gabim');
    } finally {
      setEmailTesting(false);
    }
  };

  // POK Config Functions
  const fetchPokConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/pok-config`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setPokConfig({
          key_id: data.key_id || '',
          key_secret: '', // Never received from backend for security
          merchant_id: data.merchant_id || '',
          is_sandbox: data.is_sandbox ?? true,
          webhook_url: data.webhook_url || '',
          redirect_url: data.redirect_url || '',
          fail_redirect_url: data.fail_redirect_url || '',
          auto_capture: data.auto_capture ?? true,
          is_configured: data.is_configured
        });
      }
    } catch (error) {
      console.error('Error fetching POK config:', error);
    }
  };

  const updatePokConfig = (field, value) => { setPokConfig(prev => ({ ...prev, [field]: value })); setPokModified(true); };

  const savePokConfig = async () => {
    try {
      setPokLoading(true);
      const response = await fetch(`${API_URL}/api/admin/pok-config`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(pokConfig)
      });
      if (!response.ok) throw new Error('Failed');
      toast.success('Konfigurimi POK u ruajt');
      setPokModified(false);
      fetchPokConfig();
    } catch (error) {
      toast.error('Gabim gjatë ruajtjes');
    } finally {
      setPokLoading(false);
    }
  };

  const testPokConfig = async () => {
    try {
      setPokTesting(true);
      setPokTestResult(null);
      const response = await fetch(`${API_URL}/api/admin/pok-config/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const result = await response.json();
      setPokTestResult(result);
      toast[result.success ? 'success' : 'error'](result.success ? 'POK lidhur me sukses!' : result.error);
    } catch (error) {
      setPokTestResult({ success: false, error: error.message });
      toast.error('Gabim');
    } finally {
      setPokTesting(false);
    }
  };

  // App Version Config Functions
  const fetchAppVersionConfig = async () => {
    try {
      setAppVersionLoading(true);
      const response = await fetch(`${API_URL}/api/admin/app-version-config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAppVersionConfig({
          min_version: data.min_version || '1.0.0',
          latest_version: data.latest_version || '1.0.0',
          update_url_android: data.update_url_android || '',
          update_url_ios: data.update_url_ios || '',
          force_update: data.force_update ?? true,
          update_message: data.update_message || 'Keni një version të vjetër të aplikacionit. Ju lutem përditësoni për të vazhduar.'
        });
      }
    } catch (error) {
      console.error('Error fetching app version config:', error);
    } finally {
      setAppVersionLoading(false);
    }
  };

  const saveAppVersionConfig = async () => {
    try {
      setAppVersionLoading(true);
      const response = await fetch(`${API_URL}/api/admin/app-version-config`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(appVersionConfig)
      });
      if (response.ok) {
        toast.success('Konfigurimi i versionit u ruajt me sukses!');
        setAppVersionModified(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Gabim gjatë ruajtjes');
      }
    } catch (error) {
      toast.error('Gabim: ' + error.message);
    } finally {
      setAppVersionLoading(false);
    }
  };

  const incrementVersion = (version, part) => {
    const parts = version.split('.').map(Number);
    if (part === 'major') {
      parts[0] += 1;
      parts[1] = 0;
      parts[2] = 0;
    } else if (part === 'minor') {
      parts[1] += 1;
      parts[2] = 0;
    } else {
      parts[2] += 1;
    }
    return parts.join('.');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  // Render content based on active tab
  const renderContent = () => {
    switch (activeTab) {
      case 'system':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-white flex items-center gap-2">
                  <Globe className="w-4 h-4 text-blue-400" /> Rajoni & Koha
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-zinc-500">Zona Kohore</Label>
                    <Select value={getSettingValue('system.timezone') || 'Europe/Tirane'} onValueChange={(v) => updateLocalSetting('system.timezone', v)} disabled={!canEdit}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{timezones.map(tz => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-500">Monedha</Label>
                    <Select value={getSettingValue('system.currency') || 'LEK'} onValueChange={(v) => updateLocalSetting('system.currency', v)} disabled={!canEdit}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="LEK">LEK - Lek Shqiptar</SelectItem>
                        <SelectItem value="EUR">EUR - Euro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center justify-between py-2 px-3 bg-zinc-800/50 rounded-lg">
                  <div><p className="text-sm text-white">Format 24 Orë</p><p className="text-xs text-zinc-500">Përdor formatin 24h</p></div>
                  <Switch checked={getSettingValue('system.use_24h_format') !== false} onCheckedChange={(v) => updateLocalSetting('system.use_24h_format', v)} disabled={!canEdit} />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-white flex items-center gap-2"><Server className="w-4 h-4 text-purple-400" /> Sistemi</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {[
                  { key: 'system.maintenance_mode', label: 'Modaliteti Mirëmbajtje', desc: 'Bllokon akseset e jashtme' },
                  { key: 'system.allow_registration', label: 'Lejo Regjistrimin', desc: 'Përdoruesit mund të regjistrohen', default: true },
                  { key: 'system.debug_mode', label: 'Debug Mode', desc: 'Logim i detajuar' },
                ].map(item => (
                  <div key={item.key} className="flex items-center justify-between py-2 px-3 bg-zinc-800/50 rounded-lg">
                    <div><p className="text-sm text-white">{item.label}</p><p className="text-xs text-zinc-500">{item.desc}</p></div>
                    <Switch checked={item.default ? getSettingValue(item.key) !== false : getSettingValue(item.key) === true} onCheckedChange={(v) => updateLocalSetting(item.key, v)} disabled={!canEdit} />
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        );

      case 'pricing':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {[
              { icon: Zap, color: 'yellow', title: 'Çmimi i Energjisë', key: 'pricing.energy_price', unit: 'LEK/kWh', desc: 'Çmimi bazë për çdo kWh' },
              { icon: CreditCard, color: 'green', title: 'Tarifa e Fillimit', key: 'pricing.connection_fee', unit: 'LEK', desc: 'Tarifë fikse në fillim' },
              { icon: Clock, color: 'red', title: 'Tarifa Qëndrimi', key: 'pricing.idle_fee_per_minute', unit: 'LEK/min', desc: 'Pas përfundimit të karikimit' },
            ].map(item => (
              <Card key={item.key} className="bg-zinc-900/50 border-zinc-800">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base text-white flex items-center gap-2">
                    <item.icon className={`w-4 h-4 text-${item.color}-400`} /> {item.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex gap-2">
                    <Input type="number" value={getSettingValue(item.key) || ''} onChange={(e) => updateLocalSetting(item.key, parseFloat(e.target.value))} className="bg-zinc-800 border-zinc-700" disabled={!canEdit} />
                    <span className="flex items-center px-3 bg-zinc-800 border border-zinc-700 rounded-md text-sm text-zinc-400">{item.unit}</span>
                  </div>
                  <p className="text-xs text-zinc-500">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        );

      case 'payments':
        const paymentProcessors = [
          { 
            id: 'stripe', 
            name: 'Stripe', 
            key: 'payments.stripe_enabled', 
            color: '#635bff', 
            initial: 'S',
            description: 'Paguaj me kartë krediti/debiti përmes Stripe',
            dashboardUrl: 'https://dashboard.stripe.com'
          },
          { 
            id: 'paypal', 
            name: 'PayPal', 
            key: 'payments.paypal_enabled', 
            color: '#003087', 
            initial: 'P',
            description: 'Paguaj me PayPal ose kartë',
            dashboardUrl: 'https://developer.paypal.com'
          },
          { 
            id: 'pok', 
            name: 'POK Payments', 
            key: 'payments.pok_enabled', 
            color: '#00a651', 
            initial: 'POK',
            description: 'Procesor pagesash për Shqipërinë',
            dashboardUrl: 'https://dashboard.pokpay.io'
          },
          { 
            id: 'cash', 
            name: 'Cash', 
            key: 'payments.cash_topup_enabled', 
            color: '#22c55e', 
            icon: Wallet, 
            default: true,
            description: 'Mbushje nga operatori ose zyra',
            noConfig: true
          },
        ];

        return (
          <div className="space-y-3">
            {paymentProcessors.map(processor => {
              const isExpanded = expandedProcessor === processor.id;
              const isEnabled = processor.default 
                ? getSettingValue(processor.key) !== false 
                : getSettingValue(processor.key) === true;
              
              return (
                <Card 
                  key={processor.id} 
                  className={`bg-zinc-900/50 border-zinc-800 transition-all ${isExpanded ? 'ring-1 ring-zinc-600' : ''}`}
                >
                  {/* Header - Clickable to expand */}
                  <CardHeader 
                    className="pb-3 cursor-pointer hover:bg-zinc-800/30 transition-colors"
                    onClick={() => !processor.noConfig && setExpandedProcessor(isExpanded ? null : processor.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center" 
                          style={{ backgroundColor: `${processor.color}15` }}
                        >
                          {processor.icon ? (
                            <processor.icon className="w-5 h-5" style={{ color: processor.color }} />
                          ) : (
                            <span style={{ color: processor.color }} className="font-bold text-sm">{processor.initial}</span>
                          )}
                        </div>
                        <div>
                          <CardTitle className="text-base text-white flex items-center gap-2">
                            {processor.name}
                            {!processor.noConfig && (
                              <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                            )}
                          </CardTitle>
                          <CardDescription className="text-xs">{processor.description}</CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                        <span className={`px-2 py-0.5 rounded text-xs ${isEnabled ? 'bg-green-500/10 text-green-400' : 'bg-zinc-700 text-zinc-400'}`}>
                          {isEnabled ? 'Aktiv' : 'Joaktiv'}
                        </span>
                        <Switch 
                          checked={isEnabled} 
                          onCheckedChange={(v) => updateLocalSetting(processor.key, v)} 
                          disabled={!canEdit} 
                        />
                      </div>
                    </div>
                  </CardHeader>

                  {/* Expanded Config Panel */}
                  {isExpanded && !processor.noConfig && (
                    <CardContent className="border-t border-zinc-800 pt-4 space-y-4">
                      {/* Stripe Config */}
                      {processor.id === 'stripe' && (
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label className="text-xs text-zinc-500">Publishable Key</Label>
                              <Input 
                                value={stripeConfig.publishable_key} 
                                onChange={(e) => { setStripeConfig(prev => ({...prev, publishable_key: e.target.value})); setStripeModified(true); }}
                                className="bg-zinc-800 border-zinc-700 mt-1 font-mono text-sm" 
                                placeholder="pk_test_xxx..." 
                                disabled={!canEdit} 
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-zinc-500">Secret Key</Label>
                              <div className="relative mt-1">
                                <Input 
                                  type={showStripeSecret ? 'text' : 'password'} 
                                  value={stripeConfig.secret_key} 
                                  onChange={(e) => { setStripeConfig(prev => ({...prev, secret_key: e.target.value})); setStripeModified(true); }}
                                  className="bg-zinc-800 border-zinc-700 pr-10 font-mono text-sm" 
                                  placeholder="sk_test_xxx..." 
                                  disabled={!canEdit} 
                                />
                                <button type="button" onClick={() => setShowStripeSecret(!showStripeSecret)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
                                  {showStripeSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                              </div>
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-zinc-500">Webhook Secret</Label>
                            <Input 
                              value={stripeConfig.webhook_secret} 
                              onChange={(e) => { setStripeConfig(prev => ({...prev, webhook_secret: e.target.value})); setStripeModified(true); }}
                              className="bg-zinc-800 border-zinc-700 mt-1 font-mono text-sm" 
                              placeholder="whsec_xxx..." 
                              disabled={!canEdit} 
                            />
                          </div>
                          <div className="flex items-center gap-2 py-2 px-3 bg-zinc-800/50 rounded-lg border border-zinc-700 w-fit">
                            <Shield className="w-4 h-4 text-zinc-500" />
                            <span className="text-sm text-zinc-400">Test Mode</span>
                            <Switch 
                              checked={stripeConfig.is_sandbox} 
                              onCheckedChange={(v) => { setStripeConfig(prev => ({...prev, is_sandbox: v})); setStripeModified(true); }}
                              disabled={!canEdit} 
                              className="ml-2" 
                            />
                          </div>
                          <div className="flex items-center gap-3 pt-2">
                            <Button disabled={!canEdit || !stripeModified} className="bg-[#635bff] hover:bg-[#5851db] text-white">
                              <Save className="w-4 h-4 mr-2" /> Ruaj Stripe
                            </Button>
                          </div>
                          <div className="text-xs text-zinc-500 pt-2 border-t border-zinc-800">
                            <p className="flex items-center gap-1">
                              <Info className="w-3 h-3" /> 
                              Kredencialet mund t'i merrni nga <a href={processor.dashboardUrl} target="_blank" rel="noopener noreferrer" className="text-[#635bff] hover:underline">Stripe Dashboard</a>
                            </p>
                          </div>
                        </>
                      )}

                      {/* PayPal Config */}
                      {processor.id === 'paypal' && (
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label className="text-xs text-zinc-500">Client ID</Label>
                              <Input 
                                value={paypalConfig.client_id} 
                                onChange={(e) => { setPaypalConfig(prev => ({...prev, client_id: e.target.value})); setPaypalModified(true); }}
                                className="bg-zinc-800 border-zinc-700 mt-1 font-mono text-sm" 
                                placeholder="AXxxxx..." 
                                disabled={!canEdit} 
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-zinc-500">Client Secret</Label>
                              <div className="relative mt-1">
                                <Input 
                                  type={showPaypalSecret ? 'text' : 'password'} 
                                  value={paypalConfig.client_secret} 
                                  onChange={(e) => { setPaypalConfig(prev => ({...prev, client_secret: e.target.value})); setPaypalModified(true); }}
                                  className="bg-zinc-800 border-zinc-700 pr-10 font-mono text-sm" 
                                  placeholder="ELxxxx..." 
                                  disabled={!canEdit} 
                                />
                                <button type="button" onClick={() => setShowPaypalSecret(!showPaypalSecret)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
                                  {showPaypalSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 py-2 px-3 bg-zinc-800/50 rounded-lg border border-zinc-700 w-fit">
                            <Shield className="w-4 h-4 text-zinc-500" />
                            <span className="text-sm text-zinc-400">Sandbox Mode</span>
                            <Switch 
                              checked={paypalConfig.is_sandbox} 
                              onCheckedChange={(v) => { setPaypalConfig(prev => ({...prev, is_sandbox: v})); setPaypalModified(true); }}
                              disabled={!canEdit} 
                              className="ml-2" 
                            />
                          </div>
                          <div className="flex items-center gap-3 pt-2">
                            <Button disabled={!canEdit || !paypalModified} className="bg-[#003087] hover:bg-[#001f5c] text-white">
                              <Save className="w-4 h-4 mr-2" /> Ruaj PayPal
                            </Button>
                          </div>
                          <div className="text-xs text-zinc-500 pt-2 border-t border-zinc-800">
                            <p className="flex items-center gap-1">
                              <Info className="w-3 h-3" /> 
                              Kredencialet mund t'i merrni nga <a href={processor.dashboardUrl} target="_blank" rel="noopener noreferrer" className="text-[#003087] hover:underline">PayPal Developer</a>
                            </p>
                          </div>
                        </>
                      )}

                      {/* POK Config */}
                      {processor.id === 'pok' && (
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <Label className="text-xs text-zinc-500">Key ID</Label>
                              <Input value={pokConfig.key_id} onChange={(e) => updatePokConfig('key_id', e.target.value)} className="bg-zinc-800 border-zinc-700 mt-1 font-mono text-sm" placeholder="pk_xxx..." disabled={!canEdit} />
                            </div>
                            <div>
                              <Label className="text-xs text-zinc-500">Key Secret</Label>
                              <div className="relative mt-1">
                                <Input type={showPokSecret ? 'text' : 'password'} value={pokConfig.key_secret} onChange={(e) => updatePokConfig('key_secret', e.target.value)} className="bg-zinc-800 border-zinc-700 pr-10 font-mono text-sm" placeholder="sk_xxx..." disabled={!canEdit} />
                                <button type="button" onClick={() => setShowPokSecret(!showPokSecret)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
                                  {showPokSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs text-zinc-500">Merchant ID</Label>
                              <Input value={pokConfig.merchant_id} onChange={(e) => updatePokConfig('merchant_id', e.target.value)} className="bg-zinc-800 border-zinc-700 mt-1 font-mono text-sm" placeholder="merchant_xxx..." disabled={!canEdit} />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-zinc-800">
                            <div>
                              <Label className="text-xs text-zinc-500">Redirect URL (pas pagesës së suksesshme)</Label>
                              <Input value={pokConfig.redirect_url} onChange={(e) => updatePokConfig('redirect_url', e.target.value)} className="bg-zinc-800 border-zinc-700 mt-1 text-sm" placeholder="albmove://payment/success" disabled={!canEdit} />
                            </div>
                            <div>
                              <Label className="text-xs text-zinc-500">Fail Redirect URL (pas dështimit)</Label>
                              <Input value={pokConfig.fail_redirect_url} onChange={(e) => updatePokConfig('fail_redirect_url', e.target.value)} className="bg-zinc-800 border-zinc-700 mt-1 text-sm" placeholder="albmove://payment/failed" disabled={!canEdit} />
                            </div>
                          </div>

                          <div className="flex items-center gap-6 pt-2">
                            <div className="flex items-center gap-2 py-2 px-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
                              <Shield className="w-4 h-4 text-zinc-500" /><span className="text-sm text-zinc-400">Sandbox</span>
                              <Switch checked={pokConfig.is_sandbox} onCheckedChange={(v) => updatePokConfig('is_sandbox', v)} disabled={!canEdit} className="ml-2" />
                            </div>
                            <div className="flex items-center gap-2 py-2 px-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
                              <Zap className="w-4 h-4 text-zinc-500" /><span className="text-sm text-zinc-400">Auto Capture</span>
                              <Switch checked={pokConfig.auto_capture} onCheckedChange={(v) => updatePokConfig('auto_capture', v)} disabled={!canEdit} className="ml-2" />
                            </div>
                          </div>

                          {pokTestResult && !pokTestResult.success && (
                            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg"><p className="text-sm text-red-400"><strong>Gabim:</strong> {pokTestResult.error}</p></div>
                          )}

                          <div className="flex items-center gap-3 pt-2">
                            <Button onClick={testPokConfig} disabled={pokTesting || !canEdit || !pokConfig.key_id} variant="outline" className="border-zinc-700">
                              {pokTesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Testo Lidhjen
                            </Button>
                            <Button onClick={savePokConfig} disabled={pokLoading || !canEdit || !pokModified} className="bg-[#00a651] hover:bg-[#008c45] text-white">
                              {pokLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Ruaj POK
                            </Button>
                            {pokTestResult?.success && (
                              <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-green-500/10 text-green-400">
                                <CheckCircle className="w-3.5 h-3.5" /> Lidhur
                              </span>
                            )}
                          </div>

                          <div className="text-xs text-zinc-500 pt-2 border-t border-zinc-800">
                            <p className="flex items-center gap-1"><Info className="w-3 h-3" /> Kredencialet mund t'i merrni nga paneli juaj POK Payments në <a href={processor.dashboardUrl} target="_blank" rel="noopener noreferrer" className="text-[#00a651] hover:underline">dashboard.pokpay.io</a></p>
                          </div>
                        </>
                      )}
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>
        );

      case 'ocpp':
        return (
          <div className="space-y-4">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-white flex items-center gap-2">
                  <Zap className="w-4 h-4 text-yellow-400" /> Komandat OCPP
                  <span className="ml-auto text-xs text-zinc-500 font-normal">OCPP 1.6J</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {OCPP_COMMANDS.map(cmd => (
                    <div key={cmd.id} className={`p-3 rounded-lg border transition-all hover:border-zinc-600 group ${
                      cmd.type === 'danger' ? 'bg-red-500/5 border-red-500/20' :
                      cmd.type === 'warning' ? 'bg-yellow-500/5 border-yellow-500/20' :
                      cmd.type === 'info' ? 'bg-blue-500/5 border-blue-500/20' :
                      cmd.type === 'primary' ? 'bg-[#afd039]/5 border-[#afd039]/20' : 'bg-zinc-800/50 border-zinc-700'
                    }`}>
                      <div className="flex items-start gap-2">
                        <cmd.icon className={`w-4 h-4 mt-0.5 ${
                          cmd.type === 'danger' ? 'text-red-400' : cmd.type === 'warning' ? 'text-yellow-400' :
                          cmd.type === 'info' ? 'text-blue-400' : cmd.type === 'primary' ? 'text-[#afd039]' : 'text-zinc-400'
                        }`} />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-white">{cmd.name}</p>
                          <p className="text-xs text-zinc-500">{cmd.description}</p>
                        </div>
                        <HelpCircle className="w-3.5 h-3.5 text-zinc-600 group-hover:text-zinc-400" />
                      </div>
                      <p className="text-xs text-zinc-600 mt-2 hidden group-hover:block">{cmd.details}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-white flex items-center gap-2"><Database className="w-4 h-4 text-purple-400" /> Parametrat Kryesorë</CardTitle>
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-zinc-800">
                    <th className="text-left py-2 px-3 text-xs text-zinc-500 uppercase">Çelësi</th>
                    <th className="text-left py-2 px-3 text-xs text-zinc-500 uppercase">Përshkrimi</th>
                    <th className="text-center py-2 px-3 text-xs text-zinc-500 uppercase w-16">R/W</th>
                  </tr></thead>
                  <tbody>
                    {OCPP_CONFIG_KEYS.map((cfg, idx) => (
                      <tr key={cfg.key} className={`border-b border-zinc-800/50 ${idx % 2 === 0 ? 'bg-zinc-900/30' : ''}`}>
                        <td className="py-2 px-3"><code className="text-xs font-mono text-[#afd039] bg-[#afd039]/10 px-1.5 py-0.5 rounded">{cfg.key}</code></td>
                        <td className="py-2 px-3 text-zinc-300">{cfg.description}</td>
                        <td className="py-2 px-3 text-center"><span className={`text-xs px-1.5 py-0.5 rounded ${cfg.rw === 'RW' ? 'bg-green-500/10 text-green-400' : 'bg-zinc-700 text-zinc-400'}`}>{cfg.rw}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-3"><CardTitle className="text-base text-white flex items-center gap-2"><Shield className="w-4 h-4 text-green-400" /> Cilësimet Lidhje</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { key: 'ocpp.heartbeat_interval', label: 'Heartbeat (s)', default: '300', desc: 'Intervali heartbeat' },
                    { key: 'ocpp.ws_ping_interval', label: 'WS Ping (s)', default: '30', desc: 'Ping për lidhje' },
                    { key: 'ocpp.connection_timeout', label: 'Timeout (s)', default: '60', desc: 'Para offline' },
                  ].map(item => (
                    <div key={item.key}>
                      <Label className="text-xs text-zinc-500">{item.label}</Label>
                      <Input type="number" value={getSettingValue(item.key) || item.default} onChange={(e) => updateLocalSetting(item.key, parseInt(e.target.value))} className="bg-zinc-800 border-zinc-700 mt-1 font-mono" disabled={!canEdit} />
                      <p className="text-xs text-zinc-600 mt-1">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'notifications':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-3"><CardTitle className="text-base text-white flex items-center gap-2"><Smartphone className="w-4 h-4 text-blue-400" /> Push (Mobile)</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {[
                  { key: 'push.session_start', label: 'Fillimi Sesionit' },
                  { key: 'push.session_end', label: 'Përfundimi Sesionit' },
                  { key: 'push.low_balance', label: 'Balancë e Ulët' },
                  { key: 'push.rfid_registered', label: 'RFID Regjistruar' },
                ].map(item => (
                  <div key={item.key} className="flex items-center justify-between py-2 px-3 bg-zinc-800/50 rounded-lg">
                    <p className="text-sm text-white">{item.label}</p>
                    <Switch checked={getSettingValue(item.key) !== false} onCheckedChange={(v) => updateLocalSetting(item.key, v)} disabled={!canEdit} />
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-3"><CardTitle className="text-base text-white flex items-center gap-2"><Mail className="w-4 h-4 text-purple-400" /> Email (Admin)</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {[
                  { key: 'email.charger_offline', label: 'Karikues Offline' },
                  { key: 'email.charger_fault', label: 'Defekt Karikuesi' },
                  { key: 'email.daily_report', label: 'Raport Ditor' },
                ].map(item => (
                  <div key={item.key} className="flex items-center justify-between py-2 px-3 bg-zinc-800/50 rounded-lg">
                    <p className="text-sm text-white">{item.label}</p>
                    <Switch checked={getSettingValue(item.key) !== false} onCheckedChange={(v) => updateLocalSetting(item.key, v)} disabled={!canEdit} />
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        );

      case 'topup':
        return (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-white flex items-center gap-2"><Wallet className="w-4 h-4 text-green-400" /> Paketat e Mbushjes</CardTitle>
              <CardDescription>1 Kredit = 1 LEK. Këto paketa shfaqen në aplikacionin mobil.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs text-zinc-500">Paketat e shpejta (Quick Amounts)</Label>
                <Input
                  value={getSettingValue('topup.quick_amounts') || '1000,2000,5000,10000'}
                  onChange={(e) => updateLocalSetting('topup.quick_amounts', e.target.value)}
                  className="bg-zinc-800 border-zinc-700 mt-1 font-mono"
                  placeholder="1000,2000,5000,10000"
                  disabled={!canEdit}
                />
                <p className="text-xs text-zinc-500 mt-1">Ndaj vlerat me presje. Shembull: 1000,2000,5000,10000</p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {(getSettingValue('topup.quick_amounts') || '1000,2000,5000,10000').split(',').map((amountStr, idx) => {
                  const amount = parseInt(amountStr.trim());
                  if (isNaN(amount)) return null;
                  return (
                    <div key={idx} className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700 text-center hover:border-[#afd039]/50 transition-colors">
                      <p className="text-2xl font-semibold text-white">{amount.toLocaleString()}</p>
                      <p className="text-xs text-zinc-500 mt-1">Kredite</p>
                    </div>
                  );
                })}
              </div>

              <div className="grid grid-cols-2 gap-4 pt-2 border-t border-zinc-800">
                <div>
                  <Label className="text-xs text-zinc-500">Shuma Minimale (LEK)</Label>
                  <Input
                    type="number"
                    value={getSettingValue('topup.min_amount') || 100}
                    onChange={(e) => updateLocalSetting('topup.min_amount', parseInt(e.target.value))}
                    className="bg-zinc-800 border-zinc-700 mt-1 font-mono"
                    disabled={!canEdit}
                  />
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">Shuma Maksimale (LEK)</Label>
                  <Input
                    type="number"
                    value={getSettingValue('topup.max_amount') || 50000}
                    onChange={(e) => updateLocalSetting('topup.max_amount', parseInt(e.target.value))}
                    className="bg-zinc-800 border-zinc-700 mt-1 font-mono"
                    disabled={!canEdit}
                  />
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between py-3 px-4 bg-zinc-800/50 rounded-lg">
                <div><p className="text-sm text-white">Printo Automatikisht</p><p className="text-xs text-zinc-500">Hap dialogun pas mbushjes</p></div>
                <Switch checked={getSettingValue('topup.auto_print') === true} onCheckedChange={(v) => updateLocalSetting('topup.auto_print', v)} disabled={!canEdit} />
              </div>
            </CardContent>
          </Card>
        );

      case 'email':
        return (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center"><Mail className="w-5 h-5 text-purple-400" /></div>
                  <div><CardTitle className="text-base text-white">SMTP</CardTitle><CardDescription>Kredencialet për dërgim</CardDescription></div>
                </div>
                {emailTestResult && (
                  <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs ${emailTestResult.success ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                    {emailTestResult.success ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                    {emailTestResult.success ? 'OK' : 'Gabim'}
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                  <Label className="text-xs text-zinc-500">SMTP Host</Label>
                  <Input value={emailConfig.smtp_host} onChange={(e) => updateEmailConfig('smtp_host', e.target.value)} className="bg-zinc-800 border-zinc-700 mt-1 font-mono text-sm" placeholder="mail.domain.al" disabled={!canEdit} />
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">Port</Label>
                  <Input type="number" value={emailConfig.smtp_port} onChange={(e) => updateEmailConfig('smtp_port', parseInt(e.target.value))} className="bg-zinc-800 border-zinc-700 mt-1 font-mono" disabled={!canEdit} />
                </div>
                <div className="flex items-end">
                  <div className="flex items-center gap-2 py-2 px-3 bg-zinc-800/50 rounded-lg border border-zinc-700 w-full">
                    <Lock className="w-4 h-4 text-zinc-500" /><span className="text-sm text-zinc-400">SSL</span>
                    <Switch checked={emailConfig.smtp_use_ssl} onCheckedChange={(v) => updateEmailConfig('smtp_use_ssl', v)} disabled={!canEdit} className="ml-auto" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label className="text-xs text-zinc-500">Username</Label><Input value={emailConfig.smtp_user} onChange={(e) => updateEmailConfig('smtp_user', e.target.value)} className="bg-zinc-800 border-zinc-700 mt-1" disabled={!canEdit} /></div>
                <div>
                  <Label className="text-xs text-zinc-500">Password</Label>
                  <div className="relative mt-1">
                    <Input type={showPassword ? 'text' : 'password'} value={emailConfig.smtp_password} onChange={(e) => updateEmailConfig('smtp_password', e.target.value)} className="bg-zinc-800 border-zinc-700 pr-10" disabled={!canEdit} />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t border-zinc-800">
                <div><Label className="text-xs text-zinc-500">From Email</Label><Input value={emailConfig.from_email} onChange={(e) => updateEmailConfig('from_email', e.target.value)} className="bg-zinc-800 border-zinc-700 mt-1" disabled={!canEdit} /></div>
                <div><Label className="text-xs text-zinc-500">From Name</Label><Input value={emailConfig.from_name} onChange={(e) => updateEmailConfig('from_name', e.target.value)} className="bg-zinc-800 border-zinc-700 mt-1" disabled={!canEdit} /></div>
                <div><Label className="text-xs text-zinc-500">Admin Email</Label><Input value={emailConfig.admin_email} onChange={(e) => updateEmailConfig('admin_email', e.target.value)} className="bg-zinc-800 border-zinc-700 mt-1" disabled={!canEdit} /></div>
              </div>

              {emailTestResult && !emailTestResult.success && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg"><p className="text-sm text-red-400"><strong>Gabim:</strong> {emailTestResult.error}</p></div>
              )}

              <div className="flex items-center gap-3 pt-2">
                <Button onClick={testEmailConfig} disabled={emailTesting || !canEdit || !emailConfig.smtp_host} variant="outline" className="border-zinc-700">
                  {emailTesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Testo
                </Button>
                <Button onClick={saveEmailConfig} disabled={emailLoading || !canEdit || !emailModified} className="bg-[#afd039] hover:bg-[#9ec02e] text-zinc-900">
                  {emailLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Ruaj
                </Button>
              </div>
            </CardContent>
          </Card>
        );

      case 'connectors':
        return (
          <div className="space-y-4" data-testid="connector-types-settings">
            {/* Header */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg text-white flex items-center gap-2">
                      <Plug className="w-5 h-5 text-blue-400" />
                      Llojet e Konektorëve
                    </CardTitle>
                    <CardDescription className="mt-1">
                      Menaxho llojet e portave të karikimit
                    </CardDescription>
                  </div>
                  <Button 
                    onClick={() => setShowAddConnectorType(true)} 
                    className="bg-[#afd039] hover:bg-[#9ec02e] text-zinc-900"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Shto Lloj
                  </Button>
                </div>
              </CardHeader>
            </Card>

            {/* Add New Form */}
            {showAddConnectorType && (
              <Card className="border-[#afd039] bg-[#afd039]/5">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-[#afd039]">Shto Lloj të Ri</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <Label className="text-xs">Emri *</Label>
                      <Input
                        value={newConnectorType.name}
                        onChange={(e) => setNewConnectorType({ ...newConnectorType, name: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 mt-1"
                        placeholder="Type 2"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Kodi *</Label>
                      <Input
                        value={newConnectorType.code}
                        onChange={(e) => setNewConnectorType({ ...newConnectorType, code: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 mt-1 font-mono"
                        placeholder="Type2"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Fuqia (kW)</Label>
                      <Input
                        type="number"
                        value={newConnectorType.max_power_kw}
                        onChange={(e) => setNewConnectorType({ ...newConnectorType, max_power_kw: parseFloat(e.target.value) || 0 })}
                        className="bg-zinc-800 border-zinc-700 mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Rrymë</Label>
                      <Select value={newConnectorType.current_type} onValueChange={(v) => setNewConnectorType({ ...newConnectorType, current_type: v })}>
                        <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="AC">AC</SelectItem>
                          <SelectItem value="DC">DC</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Përshkrimi</Label>
                    <Input
                      value={newConnectorType.description || ''}
                      onChange={(e) => setNewConnectorType({ ...newConnectorType, description: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 mt-1"
                      placeholder="Përshkrim..."
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => saveConnectorType(newConnectorType, true)} 
                      disabled={!newConnectorType.name || !newConnectorType.code}
                      size="sm"
                      className="bg-[#afd039] hover:bg-[#9ec02e] text-zinc-900"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Krijo
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => { 
                        setShowAddConnectorType(false); 
                        setNewConnectorType({ name: '', code: '', description: '', max_power_kw: 22, current_type: 'AC', is_active: true }); 
                      }}
                    >
                      Anulo
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Connector Types List */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-0">
                {connectorTypesLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
                  </div>
                ) : (
                  <div className="divide-y divide-zinc-800">
                    {connectorTypes.map((ct) => (
                      <div key={ct.id} className={`p-4 ${!ct.is_active ? 'opacity-50' : ''}`}>
                        {editingConnectorType?.id === ct.id ? (
                          // Edit Mode
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                              <div>
                                <Label className="text-xs text-zinc-500">Emri</Label>
                                <Input
                                  value={editingConnectorType.name}
                                  onChange={(e) => setEditingConnectorType({ ...editingConnectorType, name: e.target.value })}
                                  className="bg-zinc-800 border-zinc-700 mt-1"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-zinc-500">Kodi</Label>
                                <Input
                                  value={editingConnectorType.code}
                                  onChange={(e) => setEditingConnectorType({ ...editingConnectorType, code: e.target.value })}
                                  className="bg-zinc-800 border-zinc-700 mt-1 font-mono"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-zinc-500">Fuqia (kW)</Label>
                                <Input
                                  type="number"
                                  value={editingConnectorType.max_power_kw}
                                  onChange={(e) => setEditingConnectorType({ ...editingConnectorType, max_power_kw: parseFloat(e.target.value) || 0 })}
                                  className="bg-zinc-800 border-zinc-700 mt-1"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-zinc-500">Rrymë</Label>
                                <Select value={editingConnectorType.current_type} onValueChange={(v) => setEditingConnectorType({ ...editingConnectorType, current_type: v })}>
                                  <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="AC">AC</SelectItem>
                                    <SelectItem value="DC">DC</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs text-zinc-500">Përshkrimi</Label>
                              <Input
                                value={editingConnectorType.description || ''}
                                onChange={(e) => setEditingConnectorType({ ...editingConnectorType, description: e.target.value })}
                                className="bg-zinc-800 border-zinc-700 mt-1"
                              />
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => saveConnectorType(editingConnectorType)} className="bg-[#afd039] hover:bg-[#9ec02e] text-zinc-900">
                                <Save className="w-4 h-4 mr-1" /> Ruaj
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setEditingConnectorType(null)}>
                                Anulo
                              </Button>
                            </div>
                          </div>
                        ) : (
                          // View Mode
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              {/* Image/Icon */}
                              <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                ct.current_type === 'DC' ? 'bg-yellow-500/10' : 'bg-blue-500/10'
                              }`}>
                                {ct.image_url ? (
                                  <img 
                                    src={ct.image_url.startsWith('http') ? ct.image_url : `${API_URL}${ct.image_url}`} 
                                    alt={ct.name} 
                                    className="w-10 h-10 object-contain" 
                                  />
                                ) : (
                                  <Zap className={`w-6 h-6 ${ct.current_type === 'DC' ? 'text-yellow-400' : 'text-blue-400'}`} />
                                )}
                              </div>
                              
                              {/* Info */}
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-white">{ct.name}</span>
                                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                    ct.current_type === 'DC' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-blue-500/20 text-blue-400'
                                  }`}>
                                    {ct.current_type}
                                  </span>
                                </div>
                                <div className="flex items-center gap-3 text-xs text-zinc-500 mt-0.5">
                                  <span className="font-mono">{ct.code}</span>
                                  <span>•</span>
                                  <span>{ct.max_power_kw} kW</span>
                                  {ct.description && (
                                    <>
                                      <span>•</span>
                                      <span className="truncate max-w-[200px]">{ct.description}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            {/* Actions */}
                            <div className="flex items-center gap-2">
                              <label className="cursor-pointer p-2 rounded hover:bg-zinc-700 text-zinc-400 hover:text-white transition-colors" title="Ngarko imazh">
                                <input
                                  type="file"
                                  accept="image/png,image/jpeg,image/webp"
                                  className="hidden"
                                  onChange={(e) => {
                                    if (e.target.files?.[0]) {
                                      uploadConnectorTypeImage(ct.id, e.target.files[0]);
                                    }
                                  }}
                                />
                                <Upload className="w-4 h-4" />
                              </label>
                              <button
                                onClick={() => setEditingConnectorType({ ...ct })}
                                className="p-2 rounded hover:bg-zinc-700 text-zinc-400 hover:text-white transition-colors"
                                title="Modifiko"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setDeleteConnectorType(ct)}
                                className="p-2 rounded hover:bg-red-500/20 text-zinc-400 hover:text-red-400 transition-colors"
                                title="Fshi"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                              <Switch 
                                checked={ct.is_active} 
                                onCheckedChange={() => toggleConnectorTypeActive(ct.id, ct.is_active)}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );

      case 'integrations':
        return (
        <>
          <Card className="bg-zinc-900/50 border-zinc-800" data-testid="integrations-settings">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <MapPin className="w-5 h-5 text-[#afd039]" />
                Google Maps API
              </CardTitle>
              <CardDescription>
                Konfiguro API Key-in e Google Maps për shfaqjen e hartave në admin panel dhe aplikacionin mobil.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-zinc-400 space-y-2">
                    <p>Për të marrë një API Key të Google Maps:</p>
                    <ol className="list-decimal list-inside space-y-1 ml-2">
                      <li>Shko te <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-[#afd039] hover:underline">Google Cloud Console</a></li>
                      <li>Krijo ose zgjidh një projekt</li>
                      <li>Aktivizo "Maps JavaScript API", "Places API" dhe "Geocoding API"</li>
                      <li>Krijo një API Key në seksionin Credentials</li>
                      <li>Shto kufizimet HTTP Referrer: <code className="bg-zinc-700 px-1 rounded">https://app.albmove.al/*</code></li>
                    </ol>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm text-zinc-400">Google Maps API Key</Label>
                <div className="relative">
                  <Input
                    type={showGoogleMapsKey ? 'text' : 'password'}
                    value={googleMapsApiKey}
                    onChange={(e) => {
                      setGoogleMapsApiKey(e.target.value);
                      setGoogleMapsModified(true);
                      setGoogleMapsTestResult(null);
                    }}
                    placeholder="AIzaSy..."
                    className="bg-zinc-800 border-zinc-700 pr-10 font-mono"
                    disabled={!canEdit}
                    data-testid="google-maps-api-key-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowGoogleMapsKey(!showGoogleMapsKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                  >
                    {showGoogleMapsKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-zinc-500">
                  Pas ruajtjes, rifresko faqen për të parë ndryshimet në hartë.
                </p>
              </div>

              {/* Test Result Messages */}
              {googleMapsTestResult && (
                <div className={`p-3 rounded-lg border ${
                  googleMapsTestResult.success 
                    ? 'bg-green-500/10 border-green-500/20' 
                    : 'bg-red-500/10 border-red-500/20'
                }`}>
                  <div className="flex items-start gap-2">
                    {googleMapsTestResult.success ? (
                      <CheckCircle className="w-4 h-4 text-green-400 mt-0.5" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-400 mt-0.5" />
                    )}
                    <div>
                      <p className={`text-sm font-medium ${googleMapsTestResult.success ? 'text-green-400' : 'text-red-400'}`}>
                        {googleMapsTestResult.success ? googleMapsTestResult.message : googleMapsTestResult.error}
                      </p>
                      {googleMapsTestResult.details && (
                        <p className="text-xs text-zinc-500 mt-1">{googleMapsTestResult.details}</p>
                      )}
                      {googleMapsTestResult.error_code && !googleMapsTestResult.success && (
                        <p className="text-xs text-zinc-600 mt-1">Kodi: {googleMapsTestResult.error_code}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3 pt-2">
                <Button
                  onClick={testGoogleMapsApiKey}
                  disabled={googleMapsTesting || !googleMapsApiKey}
                  variant="outline"
                  className="border-zinc-700"
                  data-testid="test-google-maps-key-btn"
                >
                  {googleMapsTesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Testo API Key
                </Button>
                <Button
                  onClick={saveGoogleMapsApiKey}
                  disabled={googleMapsLoading || !canEdit || !googleMapsModified}
                  className="bg-[#afd039] hover:bg-[#9ec02e] text-zinc-900"
                  data-testid="save-google-maps-key-btn"
                >
                  {googleMapsLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Ruaj API Key
                </Button>
              </div>

              {googleMapsApiKey && !googleMapsTestResult && (
                <div className="mt-4 p-3 bg-zinc-800/50 border border-zinc-700 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Info className="w-4 h-4 text-zinc-400" />
                    <span className="text-sm text-zinc-400">API Key është konfiguruar. Kliko "Testo API Key" për të verifikuar.</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* DevPOS Fiscalization Card */}
          <Card className="bg-zinc-900/50 border-zinc-800 mt-6" data-testid="devpos-settings">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-[#afd039]" />
                  <CardTitle className="text-white">DevPOS - Fiskalizimi</CardTitle>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-zinc-400">Aktivizo</span>
                  <Switch
                    checked={devposConfig.is_enabled}
                    onCheckedChange={(checked) => {
                      setDevposConfig(prev => ({ ...prev, is_enabled: checked }));
                      setDevposModified(true);
                    }}
                    disabled={!canEdit}
                  />
                </div>
              </div>
              <CardDescription>
                Konfiguro DevPOS API për fiskalizimin automatik të faturave kur bëhet TopUp me POK Payment.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Info Box */}
              <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-zinc-400 space-y-2">
                    <p><strong>Si funksionon:</strong></p>
                    <ol className="list-decimal list-inside space-y-1 ml-2">
                      <li>Klienti bën TopUp me POK Payment</li>
                      <li>Pagesa përfundon me sukses</li>
                      <li>Sistemi krijon faturë të fiskalizuar automatikisht</li>
                      <li>Fatura dërgohet me email dhe ruhet në historik</li>
                    </ol>
                    <p className="text-xs text-zinc-500 mt-2">
                      <strong>Shënim:</strong> Për individë përdoret numri i ID Card, për biznese përdoret NIPT.
                    </p>
                  </div>
                </div>
              </div>

              {/* Connection Settings */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-zinc-300 border-b border-zinc-700 pb-2">Lidhja me API</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-zinc-400">API URL</Label>
                    <Select 
                      value={devposConfig.api_url} 
                      onValueChange={(value) => {
                        setDevposConfig(prev => ({ ...prev, api_url: value }));
                        setDevposModified(true);
                      }}
                      disabled={!canEdit}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="https://online.devpos.al">Prodhim (online.devpos.al)</SelectItem>
                        <SelectItem value="https://demo.devpos.al">Demo/Test (demo.devpos.al)</SelectItem>
                        <SelectItem value="https://api.devpos.al">API (api.devpos.al)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm text-zinc-400">Tenant</Label>
                    <Input
                      value={devposConfig.tenant}
                      onChange={(e) => {
                        setDevposConfig(prev => ({ ...prev, tenant: e.target.value }));
                        setDevposModified(true);
                      }}
                      placeholder="p.sh. albmove"
                      className="bg-zinc-800 border-zinc-700"
                      disabled={!canEdit}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm text-zinc-400">Username</Label>
                    <Input
                      value={devposConfig.username}
                      onChange={(e) => {
                        setDevposConfig(prev => ({ ...prev, username: e.target.value }));
                        setDevposModified(true);
                      }}
                      placeholder="Përdoruesi DevPOS"
                      className="bg-zinc-800 border-zinc-700"
                      disabled={!canEdit}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm text-zinc-400">Password</Label>
                    <div className="relative">
                      <Input
                        type={showDevposPassword ? 'text' : 'password'}
                        value={devposConfig.password}
                        onChange={(e) => {
                          setDevposConfig(prev => ({ ...prev, password: e.target.value }));
                          setDevposModified(true);
                        }}
                        placeholder="••••••••"
                        className="bg-zinc-800 border-zinc-700 pr-10"
                        disabled={!canEdit}
                      />
                      <button
                        type="button"
                        onClick={() => setShowDevposPassword(!showDevposPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                      >
                        {showDevposPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Fiscal Codes */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-zinc-300 border-b border-zinc-700 pb-2">Kodet Fiskale</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-zinc-400">TCR Code (Kodi i Arkës)</Label>
                    <Input
                      value={devposConfig.tcr_code}
                      onChange={(e) => {
                        setDevposConfig(prev => ({ ...prev, tcr_code: e.target.value }));
                        setDevposModified(true);
                      }}
                      placeholder="p.sh. fk455st065"
                      className="bg-zinc-800 border-zinc-700 font-mono"
                      disabled={!canEdit}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm text-zinc-400">Business Unit Code</Label>
                    <Input
                      value={devposConfig.business_unit_code}
                      onChange={(e) => {
                        setDevposConfig(prev => ({ ...prev, business_unit_code: e.target.value }));
                        setDevposModified(true);
                      }}
                      placeholder="p.sh. tr758ei942"
                      className="bg-zinc-800 border-zinc-700 font-mono"
                      disabled={!canEdit}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm text-zinc-400">Operator Code</Label>
                    <Input
                      value={devposConfig.operator_code}
                      onChange={(e) => {
                        setDevposConfig(prev => ({ ...prev, operator_code: e.target.value }));
                        setDevposModified(true);
                      }}
                      placeholder="p.sh. vq961go132"
                      className="bg-zinc-800 border-zinc-700 font-mono"
                      disabled={!canEdit}
                    />
                  </div>
                </div>
              </div>

              {/* Seller Info */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-zinc-300 border-b border-zinc-700 pb-2">Të Dhënat e Shitësit (C.S.M. shpk)</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-zinc-400">Emri i Biznesit</Label>
                    <Input
                      value={devposConfig.seller_name}
                      onChange={(e) => {
                        setDevposConfig(prev => ({ ...prev, seller_name: e.target.value }));
                        setDevposModified(true);
                      }}
                      className="bg-zinc-800 border-zinc-700"
                      disabled={!canEdit}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm text-zinc-400">NIPT</Label>
                    <Input
                      value={devposConfig.seller_nuis}
                      onChange={(e) => {
                        setDevposConfig(prev => ({ ...prev, seller_nuis: e.target.value }));
                        setDevposModified(true);
                      }}
                      className="bg-zinc-800 border-zinc-700 font-mono"
                      disabled={!canEdit}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm text-zinc-400">Adresa</Label>
                    <Input
                      value={devposConfig.seller_address}
                      onChange={(e) => {
                        setDevposConfig(prev => ({ ...prev, seller_address: e.target.value }));
                        setDevposModified(true);
                      }}
                      className="bg-zinc-800 border-zinc-700"
                      disabled={!canEdit}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm text-zinc-400">Qyteti</Label>
                    <Input
                      value={devposConfig.seller_town}
                      onChange={(e) => {
                        setDevposConfig(prev => ({ ...prev, seller_town: e.target.value }));
                        setDevposModified(true);
                      }}
                      className="bg-zinc-800 border-zinc-700"
                      disabled={!canEdit}
                    />
                  </div>
                </div>
              </div>

              {/* Test Result */}
              {devposTestResult && (
                <div className={`p-3 rounded-lg border ${
                  devposTestResult.success 
                    ? 'bg-green-500/10 border-green-500/20' 
                    : 'bg-red-500/10 border-red-500/20'
                }`}>
                  <div className="flex items-start gap-2">
                    {devposTestResult.success ? (
                      <CheckCircle className="w-4 h-4 text-green-400 mt-0.5" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-400 mt-0.5" />
                    )}
                    <div>
                      <p className={`text-sm font-medium ${devposTestResult.success ? 'text-green-400' : 'text-red-400'}`}>
                        {devposTestResult.success ? devposTestResult.message : devposTestResult.error}
                      </p>
                      {devposTestResult.details && (
                        <p className="text-xs text-zinc-500 mt-1">{devposTestResult.details}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center gap-3 pt-2">
                <Button
                  onClick={testDevposConnection}
                  disabled={devposTesting || !devposConfig.tenant || !devposConfig.username || !devposConfig.password}
                  variant="outline"
                  className="border-zinc-700"
                  data-testid="test-devpos-btn"
                >
                  {devposTesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Testo Lidhjen
                </Button>
                <Button
                  onClick={saveDevposConfig}
                  disabled={devposLoading || !canEdit || !devposModified}
                  className="bg-[#afd039] hover:bg-[#9ec02e] text-zinc-900"
                  data-testid="save-devpos-btn"
                >
                  {devposLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Ruaj Konfigurimin
                </Button>
              </div>

              {/* Status indicator */}
              {devposConfig.is_enabled && devposConfig.tcr_code && (
                <div className="mt-4 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm text-emerald-400">
                      Fiskalizimi është aktivizuar. Faturat do të krijohen automatikisht për çdo TopUp të suksesshëm.
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </>
        );

      case 'appversion':
        return (
          <div className="space-y-6">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-500/20 rounded-lg">
                      <Smartphone className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <CardTitle className="text-lg text-white">Kontrolli i Versionit</CardTitle>
                      <CardDescription>
                        Menaxho versionin minimal dhe detyro përdoruesit të përditësojnë aplikacionin
                      </CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Info Box */}
                <div className="p-4 bg-amber-500/10 rounded-lg border border-amber-500/20">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-zinc-400">
                      <p><strong>Si funksionon Force Update:</strong></p>
                      <p className="mt-2">Kur përdoruesi hap aplikacionin me version më të vogël se <strong>Versioni Minimal</strong>, 
                      aplikacioni bllokohet dhe shfaqet modal-i i përditësimit derisa përdoruesi të instalojë versionin e ri.</p>
                    </div>
                  </div>
                </div>

                {/* Version Settings */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <Label className="text-sm text-zinc-400">Versioni Minimal (Min Version)</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        value={appVersionConfig.min_version}
                        onChange={(e) => {
                          setAppVersionConfig(prev => ({ ...prev, min_version: e.target.value }));
                          setAppVersionModified(true);
                        }}
                        placeholder="1.0.0"
                        className="bg-zinc-800 border-zinc-700 font-mono"
                        disabled={!canEdit}
                      />
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-zinc-700 px-2"
                          onClick={() => {
                            setAppVersionConfig(prev => ({ 
                              ...prev, 
                              min_version: incrementVersion(prev.min_version, 'patch') 
                            }));
                            setAppVersionModified(true);
                          }}
                          title="Patch +1"
                        >
                          +0.0.1
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-zinc-500">Versioni nën të cilin aplikacioni bllokohet</p>
                  </div>
                  
                  <div className="space-y-3">
                    <Label className="text-sm text-zinc-400">Versioni i Fundit (Latest Version)</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        value={appVersionConfig.latest_version}
                        onChange={(e) => {
                          setAppVersionConfig(prev => ({ ...prev, latest_version: e.target.value }));
                          setAppVersionModified(true);
                        }}
                        placeholder="1.0.0"
                        className="bg-zinc-800 border-zinc-700 font-mono"
                        disabled={!canEdit}
                      />
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-zinc-700 px-2"
                          onClick={() => {
                            setAppVersionConfig(prev => ({ 
                              ...prev, 
                              latest_version: incrementVersion(prev.latest_version, 'patch') 
                            }));
                            setAppVersionModified(true);
                          }}
                          title="Patch +1"
                        >
                          +0.0.1
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-zinc-500">Versioni më i fundit në Store</p>
                  </div>
                </div>

                {/* Store URLs */}
                <div className="space-y-4 pt-4 border-t border-zinc-800">
                  <h4 className="text-sm font-medium text-zinc-300">Link-et e Store</h4>
                  
                  <div className="space-y-3">
                    <Label className="text-sm text-zinc-400">Google Play Store URL</Label>
                    <Input
                      value={appVersionConfig.update_url_android}
                      onChange={(e) => {
                        setAppVersionConfig(prev => ({ ...prev, update_url_android: e.target.value }));
                        setAppVersionModified(true);
                      }}
                      placeholder="https://play.google.com/store/apps/details?id=..."
                      className="bg-zinc-800 border-zinc-700"
                      disabled={!canEdit}
                    />
                  </div>
                  
                  <div className="space-y-3">
                    <Label className="text-sm text-zinc-400">Apple App Store URL</Label>
                    <Input
                      value={appVersionConfig.update_url_ios}
                      onChange={(e) => {
                        setAppVersionConfig(prev => ({ ...prev, update_url_ios: e.target.value }));
                        setAppVersionModified(true);
                      }}
                      placeholder="https://apps.apple.com/app/..."
                      className="bg-zinc-800 border-zinc-700"
                      disabled={!canEdit}
                    />
                  </div>
                </div>

                {/* Update Message */}
                <div className="space-y-3 pt-4 border-t border-zinc-800">
                  <Label className="text-sm text-zinc-400">Mesazhi i Përditësimit</Label>
                  <textarea
                    value={appVersionConfig.update_message}
                    onChange={(e) => {
                      setAppVersionConfig(prev => ({ ...prev, update_message: e.target.value }));
                      setAppVersionModified(true);
                    }}
                    placeholder="Keni një version të vjetër..."
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-zinc-200 text-sm min-h-[80px] resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                    disabled={!canEdit}
                  />
                </div>

                {/* Force Update Toggle */}
                <div className="flex items-center justify-between py-3 px-4 bg-zinc-800/50 rounded-lg">
                  <div>
                    <p className="text-sm text-white font-medium">Force Update</p>
                    <p className="text-xs text-zinc-500">Bllokon aplikacionin deri në përditësim</p>
                  </div>
                  <Switch
                    checked={appVersionConfig.force_update}
                    onCheckedChange={(checked) => {
                      setAppVersionConfig(prev => ({ ...prev, force_update: checked }));
                      setAppVersionModified(true);
                    }}
                    disabled={!canEdit}
                  />
                </div>

                {/* Action Button */}
                <div className="flex items-center gap-3 pt-4">
                  <Button
                    onClick={saveAppVersionConfig}
                    disabled={appVersionLoading || !canEdit || !appVersionModified}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="save-app-version-btn"
                  >
                    {appVersionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Ruaj Konfigurimin
                  </Button>
                  {appVersionModified && (
                    <span className="text-xs text-amber-400">Ka ndryshime të paruajtura</span>
                  )}
                </div>

                {/* Current Status */}
                <div className="mt-4 p-4 bg-zinc-800/50 rounded-lg">
                  <h5 className="text-sm font-medium text-zinc-300 mb-3">Statusi Aktual</h5>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    <div>
                      <p className="text-xs text-zinc-500">Min Version</p>
                      <p className="text-lg font-mono text-emerald-400">{appVersionConfig.min_version}</p>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500">Latest</p>
                      <p className="text-lg font-mono text-blue-400">{appVersionConfig.latest_version}</p>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500">Force Update</p>
                      <p className={`text-lg font-medium ${appVersionConfig.force_update ? 'text-amber-400' : 'text-zinc-500'}`}>
                        {appVersionConfig.force_update ? 'AKTIV' : 'JO'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500">Platformat</p>
                      <p className="text-lg">
                        <span className={appVersionConfig.update_url_android ? 'text-emerald-400' : 'text-zinc-600'}>Android</span>
                        {' / '}
                        <span className={appVersionConfig.update_url_ios ? 'text-emerald-400' : 'text-zinc-600'}>iOS</span>
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-4" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center">
            <currentTabInfo.icon className="w-5 h-5 text-[#afd039]" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-white font-outfit">{currentTabInfo.title}</h1>
            <p className="text-sm text-zinc-500">{currentTabInfo.description}</p>
          </div>
        </div>
        {(hasModifications || emailModified) && (
          <Button onClick={activeTab === 'email' ? saveEmailConfig : saveSettings} disabled={saving || emailLoading || !canEdit} className="bg-[#afd039] hover:bg-[#9ec02e] text-zinc-900">
            {(saving || emailLoading) ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Ruaj
          </Button>
        )}
      </div>

      {/* Content */}
      {renderContent()}

      {/* Delete Connector Type Dialog */}
      {deleteConnectorType && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => setDeleteConnectorType(null)} />
          <div className="relative bg-zinc-900 border border-zinc-700 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/15 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-base">Fshi Llojin e Konektorit</h3>
                <p className="text-zinc-400 text-sm">Ky veprim nuk mund të kthehet</p>
              </div>
            </div>
            <p className="text-zinc-300 text-sm mb-6">
              Jeni të sigurt që dëshironi të fshini <span className="text-white font-semibold">"{deleteConnectorType.name}"</span>?
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 border-zinc-700"
                onClick={() => setDeleteConnectorType(null)}
                disabled={deletingConnector}
              >
                Anulo
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                onClick={handleDeleteConnectorType}
                disabled={deletingConnector}
              >
                {deletingConnector ? 'Duke fshirë...' : 'Fshi'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
